import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useState } from 'react';
import useAuthStore from '../hooks/useAuthStore';

export default function MainPortal() {
  const navigate = useNavigate();
  const { user, theme } = useAuthStore();
  const [selectedCurriculum, setSelectedCurriculum] = useState(theme || 'icse');

  const isICSE = selectedCurriculum === 'icse';
  const primaryColor = isICSE ? 'icse-teal' : 'isc-coral';
  const secondaryColor = isICSE ? 'icse-cyan' : 'isc-purple';

  const features = [
    {
      id: 'textbooks',
      title: 'Textbook Answers',
      description: isICSE 
        ? 'Access complete solutions for ICSE Class 10 prescribed textbooks' 
        : 'Access complete solutions for ISC Class 12 prescribed textbooks',
      icon: '📚',
      gradient: `from-${primaryColor}-500 to-${secondaryColor}-500`,
    },
    {
      id: 'past-papers',
      title: 'Past Papers',
      description: isICSE
        ? 'Download and solve ICSE Class 10 previous year board examination papers'
        : 'Download and solve ISC Class 12 previous year board examination papers',
      icon: '📄',
      gradient: `from-${secondaryColor}-500 to-${primaryColor}-500`,
    },
    {
      id: 'community',
      title: 'Community Chat',
      description: isICSE
        ? 'Connect with ICSE Class 10 students in subject-specific rooms'
        : 'Connect with ISC Class 12 students in subject-specific rooms',
      icon: '💬',
      gradient: `from-${primaryColor}-600 to-${secondaryColor}-600`,
    },
    {
      id: 'upload',
      title: 'Upload Hub',
      description: isICSE
        ? 'Share your ICSE Class 10 notes and solutions with the community'
        : 'Share your ISC Class 12 notes and solutions with the community',
      icon: '📤',
      gradient: `from-${secondaryColor}-600 to-${primaryColor}-600`,
    },
    {
      id: 'resources',
      title: 'Recommended Textbooks',
      description: isICSE
        ? 'Discover highly recommended textbooks for ICSE Class 10 exams'
        : 'Discover highly recommended textbooks for ISC Class 12 exams',
      icon: '📖',
      gradient: `from-${primaryColor}-500 to-${secondaryColor}-500`,
    },
    {
      id: 'quiz',
      title: 'Practice Quiz',
      description: isICSE
        ? 'Test your knowledge with ICSE Class 10 practice questions'
        : 'Test your knowledge with ISC Class 12 practice questions',
      icon: '🧠',
      gradient: `from-${secondaryColor}-500 to-${primaryColor}-500`,
    },
  ];

  const handleFeatureClick = (featureId) => {
    switch (featureId) {
      case 'textbooks':
        navigate('/textbooks');
        break;
      case 'past-papers':
        navigate('/past-papers');
        break;
      case 'community':
        navigate('/community');
        break;
      case 'upload':
        navigate('/upload');
        break;
      case 'resources':
        navigate('/resources');
        break;
      case 'quiz':
        // For now, we'll show the quiz inline or navigate to a quiz page
        navigate('/quiz');
        break;
      default:
        break;
    }
  };

  return (
    <div className="min-h-screen w-full text-white antialiased bg-[linear-gradient(180deg,_#0d0b18_0%,_#16122c_50%,_#0b0914_100%)] relative overflow-x-hidden">
      {/* Soft, silk-like velvet glow overlay */}
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-purple-600/5 rounded-full blur-[120px] pointer-events-none" />

      <nav className="fixed top-0 left-0 right-0 z-50 glass-panel">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br from-${primaryColor}-500 to-${secondaryColor}-500 flex items-center justify-center text-white font-bold text-xl`}>
              S
            </div>
            <h1 className="text-2xl font-bold text-white">SolveICSE</h1>
          </div>

          <div className="flex items-center gap-4">
            {/* Curriculum Toggle */}
            <div className="flex items-center gap-2 bg-white/10 rounded-lg p-1">
              <button
                onClick={() => setSelectedCurriculum('icse')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  isICSE 
                    ? 'bg-gradient-to-r from-icse-teal-500 to-icse-cyan-500 text-white' 
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                ICSE (Class 10)
              </button>
              <button
                onClick={() => setSelectedCurriculum('isc')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  selectedCurriculum === 'isc' 
                    ? 'bg-gradient-to-r from-isc-coral-500 to-isc-purple-500 text-white' 
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                ISC (Class 12)
              </button>
            </div>

            <button
              onClick={() => navigate('/profile')}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span>Profile</span>
            </button>
          </div>
        </div>
      </nav>

      <main className="pt-24 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl font-bold text-white mb-4">
              Welcome back, <span className={`bg-gradient-to-r from-${primaryColor}-400 to-${secondaryColor}-400 bg-clip-text text-transparent`}>{user?.name || 'Student'}</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Your personalized learning hub for {isICSE ? 'ICSE Class 10' : 'ISC Class 12'} excellence
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                onClick={() => handleFeatureClick(feature.id)}
                className={`group relative overflow-hidden rounded-3xl p-8 cursor-pointer transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl`}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-20 group-hover:opacity-30 transition-opacity`}></div>
                <div className="absolute inset-0 glass-panel border border-white/10"></div>
                
                <div className="relative z-10">
                  <div className="text-5xl mb-4">{feature.icon}</div>
                  <h3 className="text-2xl font-bold text-white mb-2">{feature.title}</h3>
                  <p className="text-gray-300">{feature.description}</p>
                  
                  <div className={`mt-6 inline-flex items-center gap-2 text-${primaryColor}-400 font-medium group-hover:gap-3 transition-all`}>
                    <span>Explore</span>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="mt-16 glass-panel rounded-3xl p-8 border border-white/10"
          >
            <h3 className="text-2xl font-bold text-white mb-6">Quick Stats</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className={`text-4xl font-bold text-${primaryColor}-400 mb-2`}>500+</div>
                <div className="text-gray-400 text-sm">Textbook Solutions</div>
              </div>
              <div className="text-center">
                <div className={`text-4xl font-bold text-${secondaryColor}-400 mb-2`}>200+</div>
                <div className="text-gray-400 text-sm">Past Papers</div>
              </div>
              <div className="text-center">
                <div className={`text-4xl font-bold text-${primaryColor}-400 mb-2`}>10K+</div>
                <div className="text-gray-400 text-sm">Active Students</div>
              </div>
              <div className="text-center">
                <div className={`text-4xl font-bold text-${secondaryColor}-400 mb-2`}>24/7</div>
                <div className="text-gray-400 text-sm">Community Support</div>
              </div>
            </div>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
